import { toast } from 'react-toastify';
import { removeAllCookies, getUserLoginToken } from './UserInfo';
import { getCookie } from './Cookies';
import { isSessionActive, getSessionExpiry } from './sessionState';

export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.';
export const SIGNED_OUT_ELSEWHERE_MESSAGE = 'You were signed out in another tab.';
export const LOGIN_PATH = '/Account/Login';
const REDIRECT_DELAY_MS = 1500;

export const SESSION_EXPIRED_SAVE_WORK_MESSAGE = 'Your session has expired. Please log in to save your work.';

let handling = false;
let inPlaceHandler = null;
let inPlaceExpiryDispatching = false;

/** True only while an in-place session expiry is broadcasting 'userLoggedOut' (not for an explicit
 * Logout click). App uses it to keep the route tree mounted so the page keeps its unsaved state. */
export const isInPlaceReloginActive = () => inPlaceExpiryDispatching;

/** Opt a page into in-place re-login: while registered, a session expiry clears the login and calls
 * `handler` once instead of redirecting to the login page (the page keeps its unsaved state and
 * shows its own login UI). Returns the unregister function; pass it to the effect cleanup.
 * @param {() => void} handler
 * @returns {() => void}
 */
export const registerSessionExpiryHandler = (handler) => {
    inPlaceHandler = handler;
    return () => { if(inPlaceHandler === handler) inPlaceHandler = null; };
};

const isOnLoginPage = () => window.location.pathname.toLowerCase().startsWith(LOGIN_PATH.toLowerCase());

/** `/Account/Login?returnUrl=<current page>` so Logon.jsx brings the user back after signing in. */
export const buildLoginUrl = () => {
    const { pathname, search } = window.location;
    return `${LOGIN_PATH}?returnUrl=${encodeURIComponent(pathname + search)}`;
};

/** True when this tab had a signed-in session that has now ended: the login cookies are gone, or
 * the stored expiry time has passed. False for a tab that was never signed in. */
export const hasSessionEnded = () => {
    if(!isSessionActive()) return false;
    const expiresAt = getSessionExpiry();
    if(expiresAt && Date.now() >= expiresAt) return true;
    return !getCookie('persistence-key') && !getUserLoginToken();
};

/** The login session expired (API 401 or the expiry timer): clear the login, tell the user once and
 * send them to the login page, with the current page as returnUrl. Admins included.
 *
 * Does nothing when this tab never had a signed-in session (an anonymous page that called a
 * protected endpoint), when it is already handling an expiry (parallel 401s), or -- apart from
 * clearing the login -- when the user is already on the login page (no redirect loop).
 *
 * @param {string} [message] - toast text
 * @param {'expired'|'logout'} [reason] - what other tabs are told ('logout' = the user signed out)
 * @returns {boolean} true when the expiry was handled by this call
 */
export const handleSessionExpired = (message = SESSION_EXPIRED_MESSAGE, reason = 'expired') => {
    if(handling || !isSessionActive()) return false;
    handling = true;

    removeAllCookies(reason);

    // in-place re-login (e.g. the STP wizard): stay on the page, hand over to the registered handler
    if(inPlaceHandler){
        toast.error(SESSION_EXPIRED_SAVE_WORK_MESSAGE, { position: 'bottom-right', autoClose: 4000, toastId: 'session-expired' });
        inPlaceExpiryDispatching = true;
        try{ window.dispatchEvent(new Event('userLoggedOut')); }finally{ inPlaceExpiryDispatching = false; } // drop auth state (navbar, context)
        handling = false; // the login is cleared (session inactive), so repeated 401s stay no-ops
        try{ inPlaceHandler(); }catch(error){ console.error('session expiry handler failed:', error); }
        return true;
    }

    toast.error(message || SESSION_EXPIRED_MESSAGE, { position: 'bottom-right', autoClose: 4000, toastId: 'session-expired' });

    if(isOnLoginPage()){
        window.dispatchEvent(new Event('userLoggedOut'));
        handling = false;
        return true;
    }

    const loginUrl = buildLoginUrl();
    setTimeout(() => {
        // drop auth state (navbar, route tree) right before leaving, so the page does not
        // flicker to a logged-out layout while the toast is being read
        window.dispatchEvent(new Event('userLoggedOut'));
        window.location.assign(loginUrl);
        // still here after the navigation call (blocked / failed): allow a later expiry to retry
        setTimeout(() => { handling = false; }, REDIRECT_DELAY_MS);
    }, REDIRECT_DELAY_MS);
    return true;
};

/** Test helper: reset the once-only guard. */
export const resetSessionExpiryHandling = () => { handling = false; inPlaceHandler = null; inPlaceExpiryDispatching = false; };
